import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { AppointmentsTable } from "@/components/appointments/appointments-table";
import { AppointmentReviewDialog } from "@/components/appointments/appointment-review-dialog";
import { Button } from "@/components/ui/button";
import { useAppointmentRequests } from "@/hooks/use-appointment-requests";
import {
  isAppointmentId,
  useAppointmentDetail,
} from "@/hooks/use-appointment-detail";
import { usePathname, useRouter, useSearchParams } from "@/i18n/navigation";
import type { Appointment } from "@/types";

export const Route = createFileRoute("/_dashboard/appointments")({
  validateSearch: (search: Record<string, unknown>) => ({
    appointment:
      typeof search.appointment === "string" &&
      isAppointmentId(search.appointment)
        ? search.appointment
        : undefined,
  }),
  component: AppointmentsPage,
});

function AppointmentsContent() {
  const t = useTranslations("AppointmentsPage");
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawSearch = searchParams.toString();
  const rawSelectedId = searchParams.get("appointment") ?? undefined;
  const canonicalSelectedId = isAppointmentId(rawSelectedId)
    ? rawSelectedId
    : undefined;
  const selectedId = canonicalSelectedId;
  const {
    appointments,
    isLoading,
    isFetching: isListFetching,
    error: listError,
    refetch: refetchList,
  } = useAppointmentRequests({
    limit: 500,
    scope: "all",
    suppressGlobalError: true,
  });
  const initialAppointment = appointments.find((item) => item.id === selectedId);
  const {
    appointment: selectedAppointment,
    isFetching: isDetailFetching,
    isError: detailError,
    isNotFound,
    refetch: refetchDetail,
  } = useAppointmentDetail(selectedId, initialAppointment);

  useEffect(() => {
    if (pathname !== "/appointments") return;
    const expected = canonicalSelectedId
      ? `appointment=${encodeURIComponent(canonicalSelectedId)}`
      : "";
    if (rawSearch !== expected) {
      router.replace(expected ? `/appointments?${expected}` : "/appointments");
    }
  }, [canonicalSelectedId, pathname, rawSearch, router]);

  function openAppointment(appointment: Appointment) {
    router.push(`/appointments?appointment=${appointment.id}`);
  }

  function closeAppointment() {
    router.replace("/appointments");
  }

  function handleDialogOpenChange(open: boolean) {
    if (open) return;
    closeAppointment();
  }

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="sr-only">{t("title")}</h1>

        {(detailError || isNotFound) && (
          <div
            role="alert"
            className="flex flex-col items-start gap-3 rounded-md border p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="text-muted-foreground text-sm">
              {isNotFound ? t("notFound") : t("detailLoadError")}
            </p>
            <div className="flex gap-2">
              {!isNotFound && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refetchDetail()}
                  disabled={isDetailFetching}
                >
                  {isDetailFetching ? t("retrying") : t("retry")}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={closeAppointment}>
                {t("close")}
              </Button>
            </div>
          </div>
        )}

        {listError ? (
          <div
            role="alert"
            className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-md border p-6 text-center"
          >
            <p className="text-muted-foreground text-sm">
              {t("listLoadError")}
            </p>
            <Button
              variant="outline"
              onClick={() => void refetchList()}
              disabled={isListFetching}
            >
              {isListFetching ? t("retrying") : t("retry")}
            </Button>
          </div>
        ) : (
          <AppointmentsTable
            data={appointments}
            isLoading={isLoading}
            onReview={openAppointment}
          />
        )}
      </div>

      <AppointmentReviewDialog
        appointment={selectedAppointment}
        open={!!selectedId && !!selectedAppointment && !detailError}
        onOpenChange={handleDialogOpenChange}
      />
    </>
  );
}

function AppointmentsPage() {
  return (
    <Suspense>
      <AppointmentsContent />
    </Suspense>
  );
}
